import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import CustomDatePicker from "../../../../../../../components/CustomDatePicker";
import CustomTextArea from "../../../../../../../components/CustomTextArea";
import Input from "../../../../../../../components/Input";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import useSelectedPatient from "../../../../../../../hooks/local-storage/use-selected-patient";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { usePastMedicationHistoryUpdate } from "../../../../../../../queries/history/past-medication/use-patient-medical-history-update";
import { usePastMedicationHistoryCreate } from "../../../../../../../queries/history/past-medication/use-patient-medication-history-create";
import { usePastMedicalHistoryList } from "../../../../../../../queries/history/past-medication/use-patient-medical-history-list";
import {
  ExportClinicalDataToEncounterTypeEnum,
  MedicalHistory,
} from "../../../../../../../services";
import { useExportClinicalToEncounter } from "../../../../../../../queries/encounter/use-export-clinical-to-encounter";
import {
  DEF_PAGINATION,
  getErrorSnackbar,
  getSuccessSnackbar,
} from "../../../../../../../utils/utils";
import LabeledSwitch from "../../../../../../../components/LabeledSwitch";
import MaskMultiSelect from "../../../../../../../components/MaskMultiSelect";
import { getStoredRealm } from "@/utils/auth-storage";
interface UpsertMedicalHistoryDrawerProps {
  disclosureHost: DisclosureHost;
}
type MedicalHistoryType = Omit<MedicalHistory, "conditionName"> & {
  conditionName?: string;
};

const UpsertMedicalHistoryDrawer = ({
  disclosureHost,
}: UpsertMedicalHistoryDrawerProps) => {
  const prefillData = disclosureHost?.data as MedicalHistoryType;
  const [selectedPatient] = useSelectedPatient();
  const appointmentid = useQueryParams("appointmentid");
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: createPastMedicalHistory } =
    usePastMedicationHistoryCreate();
  const { mutateAsync: updatePastMedicalHistory } =
    usePastMedicationHistoryUpdate();
  const { mutateAsync: exportClinical } = useExportClinicalToEncounter();

  const isVisitNotePage = window.location.pathname.includes("/visit-note");
  const [variant, setVariant] = useState<"import" | "default">("default");
  const [selectedRecords, setSelectedRecords] = useState<any>();

  const formik = useFormik<MedicalHistoryType | Record<string, any>>({
    initialValues: prefillData || initialValues,
    validationSchema: Yup.object().shape({
      conditionName: Yup.string().trim().required().label("Condition"),
      // medicalHistoryDate: Yup.string().required().label("Onset Date"),
    }),
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });
  const { patientId } = useParams() as { patientId: any };

  const { data: medicalHistoryList, isLoading: isMedicalHistoryLoading } =
    usePastMedicalHistoryList({
      patientUuid: patientId ?? "",
      pagination: DEF_PAGINATION,
      gridSorting: [],
      archive: false,
      status: true,
      enabled: variant === "import",
    });

  const medicalHistoryOptions = useMemo(() => {
    return medicalHistoryList?.data.content.map((item: any) => ({
      value: item.uuid,
      label: item?.conditionName,
    }));
  }, [medicalHistoryList?.data.content]);

  async function handleSubmit(values?: any) {
    const patient = {
      [patientId ? patientId : selectedPatient?.uuid]: patientId
        ? ""
        : selectedPatient?.firstName + " " + selectedPatient?.lastName,
    };

    const payload = {
      medicalHistory: {
        ...values,
        patient,
        appointmentId: appointmentid ?? undefined,
      },
      xTENANTID: getStoredRealm() ?? undefined,
    };

    if (prefillData) showToastAndResetForm(updatePastMedicalHistory(payload));
    else showToastAndResetForm(createPastMedicalHistory(payload));
  }

  async function handleImportMedicalHistory() {
    const recordUuids = (selectedRecords ?? [])
      .map((record: any) => record?.value)
      .filter(Boolean);

    if (!recordUuids.length) {
      showSnackbar(getErrorSnackbar("Please select at least one record."));
      return;
    }

    showToastAndResetForm(
      exportClinical({
        type: ExportClinicalDataToEncounterTypeEnum.MedicalHistory,
        appointmentUuid: appointmentid ?? "",
        recordUuids,
        xTENANTID: getStoredRealm() ?? undefined,
      })
    );
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    apiCall
      .then((res) => {
        closeAndResetForm();

        queryClient.invalidateQueries({
          queryKey: ["patient-medical-history-list"],
        });

        showSnackbar(getSuccessSnackbar(res));
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  function closeAndResetForm() {
    formik.resetForm();
    setVariant("default");
    setSelectedRecords(undefined);
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeAndResetForm}
      dialogTitle={
        prefillData ? "Edit Past Medical History" : "Add Past Medical History"
      }
      dialogAction={
        <StyledButtonV2
          label={variant === "import" ? "Import" : "Save"}
          variant="contained"
          onClick={
            variant === "import"
              ? handleImportMedicalHistory
              : formik.submitForm
          }
        />
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "450px",
        },
      }}
    >
      {isVisitNotePage && (
        <Box
          sx={{
            pt: 1,
            px: 1.5,
          }}
        >
          <LabeledSwitch
            label="import from existing records"
            checked={variant === "import"}
            onChange={(e) => {
              setVariant(e.target.checked ? "import" : "default");
            }}
          />
        </Box>
      )}

      {variant === "import" && (
        <Grid
          container
          spacing={3}
          sx={{
            pt: 0,
          }}
        >
          <Grid size={12}>
            <MaskMultiSelect
              required
              loading={isMedicalHistoryLoading}
              label="Condition Name"
              optionWithCheckbox={true}
              placeholder="Select from Existing Medical Records"
              options={medicalHistoryOptions}
              value={selectedRecords}
              onChange={(v) => setSelectedRecords(v)}
            />
          </Grid>
        </Grid>
      )}

      {variant === "default" && (
        <Grid
          container
          spacing={3}
          sx={{
            pt: isVisitNotePage ? 1 : 2,
          }}
        >
          <Grid size={12}>
            <Input
              required
              label={"Condition Name"}
              textFieldProps={{
                placeholder: "Enter Condition Name",
                name: "conditionName",
                value: formik.values.conditionName,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              }}
              error={
                (formik.touched?.conditionName &&
                  formik.errors?.conditionName) as any
              }
            />
          </Grid>
          <Grid size={12}>
            <Grid size={12}>
              <CustomDatePicker
                // required
                // error={
                //   (formik.touched?.medicalHistoryDate &&
                //     formik.errors?.medicalHistoryDate) as any
                // }
                name="medicalHistoryDate"
                handleBlur={formik.handleBlur}
                placeholder="Choose Date"
                value={
                  formik.values.medicalHistoryDate
                    ? new Date(formik.values.medicalHistoryDate)
                    : undefined
                }
                maxDate={dayjs(new Date())}
                label="Onset Date"
                onChange={(val) =>
                  formik.setFieldValue("medicalHistoryDate", val?.toISOString())
                }
              />
            </Grid>
          </Grid>
          <Grid size={12}>
            <CustomTextArea
              label="Note"
              id="outlined-multiline-static"
              rows={5}
              variant="outlined"
              name="note"
              placeholder="Type here"
              onBlur={formik.handleBlur}
              value={formik.values?.note}
              onChange={formik.handleChange}
            />
          </Grid>
        </Grid>
      )}
    </StyledFormModal>
  );
};

export default UpsertMedicalHistoryDrawer;

// conditionName must be declared here even though it starts empty: on submit Formik
// derives `touched` from the keys present in `values`, so a field that is missing from
// initialValues never becomes touched and its validation message stays hidden.
const initialValues = {
  conditionName: "",
  medicalHistoryDate: new Date().toISOString(),
};
